import React, { useEffect, useState } from "react";
import { Collapse, List, Avatar, Spin, Tag, Popconfirm, message, Modal, Button, Input, Switch } from "antd";
import { getAllCountries, updateCity, updateCountry, createCity, createCountry } from "@/server/countries/countriesServer";

const { Panel } = Collapse;

const CountriesList = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editData, setEditData] = useState({ type: "", data: {} });
  const [addCityModalVisible, setAddCityModalVisible] = useState(false);
  const [newCityData, setNewCityData] = useState({ country: '', name: '', coordinates: ['', ''], isEnabled: true });
  const [addingCity, setAddingCity] = useState(false);
  const [addCountryModalVisible, setAddCountryModalVisible] = useState(false);
  const [newCountryData, setNewCountryData] = useState({ name: '', code: '', capital: '', phoneCode: '', currency: '', coordinates: ['', ''], isEnabled: true });
  const [addingCountry, setAddingCountry] = useState(false);

  useEffect(() => {
    fetchCountriesData();
  }, []);

  const fetchCountriesData = async () => {
    setLoading(true);
    try {
      const response = await getAllCountries();
      if (response.statusCode === 200) {
        setCountries(response.data);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateAllCitiesStatus = async (countryId, isEnabled) => {
    const country = countries.find((c) => c._id === countryId);
    if (!country) return;

    // Update all cities of the country
    for (const city of country.cities) {
      const updatedCity = { ...city, isEnabled };
      await updateCity(city._id, updatedCity);
    }

    fetchCountriesData();
  };

  const checkIfAllCitiesDisabled = (countryId) => {
    const country = countries.find((c) => c._id === countryId);
    if (!country) return true;

    return country.cities.every((city) => !city.isEnabled);
  };

  const handleToggleStatus = async (id, currentStatus, type) => {
    try {
      let updatedItem, response;
      if (type === "country") {
        const country = countries.find((c) => c._id === id);
        if (!country) throw new Error("Country not found");
  
        // Prepare the updated country object
        updatedItem = {
          name: country.name,
          isEnabled: !currentStatus,
        };
        
        // Only include coordinates if they exist and are valid
        if (Array.isArray(country.coordinates) && country.coordinates.length === 2) {
          updatedItem.coordinates = country.coordinates;
        }
  
        // Update the country
        response = await updateCountry(id, updatedItem);
  
        // Update all cities of the country
        await updateAllCitiesStatus(id, !currentStatus);
      } else {
        let city, countryOfCity;
        for (const country of countries) {
          city = country.cities.find((c) => c._id === id);
          if (city) {
            countryOfCity = country;
            break;
          }
        }
        if (!city) throw new Error("City not found");
  
        // Prepare the updated city object
        updatedItem = { 
          name: city.name,
          isEnabled: !currentStatus, 
          country: countryOfCity._id 
        };
        
        // Only include coordinates if they exist and are valid
        if (Array.isArray(city.coordinates) && city.coordinates.length === 2) {
          updatedItem.coordinates = city.coordinates;
        }
        response = await updateCity(id, updatedItem);
  
        if (response.statusCode === 200) {
          message.success(`City updated successfully`);
  
          // Fetch updated data
          const updatedCountries = await getAllCountries();
          setCountries(updatedCountries.data);
  
          // Check if the city is being enabled
          if (!currentStatus) {
            // If the city is being enabled, activate the parent country if it's not already active
            if (!countryOfCity.isEnabled) {
              const updatedCountry = { ...countryOfCity, isEnabled: true };
              await updateCountry(countryOfCity._id, updatedCountry);
              message.success("City enabled, country enabled automatically");
            }
          }
  
          // Check if all cities are disabled
          const allCitiesDisabled = updatedCountries.data.find(
            (c) => c._id === countryOfCity._id
          )?.cities.every((city) => !city.isEnabled);
  
          // If all cities are disabled, disable the country
          if (allCitiesDisabled) {
            const updatedCountry = { ...countryOfCity, isEnabled: false };
            await updateCountry(countryOfCity._id, updatedCountry);
            message.success("All cities disabled, country disabled automatically");
          }
  
          // Refresh data again
          fetchCountriesData();
        }
      }
    } catch (error) {
      console.error(`Error updating ${type}:`, error);
      message.error(`Failed to update ${type}`);
    }
  };
  

  const handleEditClick = (type, data) => {
    setEditData({ type, data });
    setEditModalVisible(true);
  };

  const handleEditSave = async () => {
    try {
      const { type, data } = editData;

      // Prepare clean update data
      const updatedData = {
        name: data.name,
        isEnabled: data.isEnabled
      };

      // Add type-specific fields
      if (type === "country") {
        if (data.code) updatedData.code = data.code;
        if (data.capital) updatedData.capital = data.capital;
        if (data.phoneCode) updatedData.phoneCode = data.phoneCode;
        if (data.currency) updatedData.currency = data.currency;
      } else {
        // For city, include country reference
        const parentCountry = countries.find(c => 
          c.cities.some(city => city._id === data._id)
        );
        if (parentCountry) {
          updatedData.country = parentCountry._id;
        }
      }

      // Only include coordinates if they are valid
      if (Array.isArray(data.coordinates) && 
          data.coordinates.length === 2 && 
          data.coordinates[0] !== null && 
          data.coordinates[1] !== null &&
          !isNaN(data.coordinates[0]) &&
          !isNaN(data.coordinates[1])) {
        updatedData.coordinates = data.coordinates;
      }

      let response;
      if (type === "country") {
        response = await updateCountry(data._id, updatedData);
      } else {
        response = await updateCity(data._id, updatedData);
      }

      if (response.statusCode === 200) {
        message.success(`${type === "country" ? "Country" : "City"} updated successfully`);
        setEditModalVisible(false);
        fetchCountriesData();
      }
    } catch (error) {
      console.error("Error updating:", error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to update";
      message.error(errorMsg);
    }
  };

  if (loading) return <Spin size="large" />;

  return (
    <>
      <Button type="primary" style={{ marginBottom: 16, marginRight: 8 }} onClick={() => setAddCountryModalVisible(true)}>
        Add Country
      </Button>
      <Button type="primary" style={{ marginBottom: 16 }} onClick={() => setAddCityModalVisible(true)}>
        Add City
      </Button>
      <Modal
        title="Add Country"
        visible={addCountryModalVisible}
        onOk={async () => {
          setAddingCountry(true);
          try {
            if (!newCountryData.name) {
              message.error('Country name is required');
              setAddingCountry(false);
              return;
            }
            const payload = {
              name: newCountryData.name,
              code: newCountryData.code || undefined,
              capital: newCountryData.capital || undefined,
              phoneCode: newCountryData.phoneCode || undefined,
              currency: newCountryData.currency || undefined,
              coordinates: (newCountryData.coordinates[0] && newCountryData.coordinates[1]) 
                ? [Number(newCountryData.coordinates[0]), Number(newCountryData.coordinates[1])]
                : undefined,
              isEnabled: newCountryData.isEnabled
            };
            // Remove undefined fields
            Object.keys(payload).forEach(key => (payload[key] === undefined || (Array.isArray(payload[key]) && payload[key].every(v => v === undefined))) && delete payload[key]);
            const res = await createCountry(payload);
            if (res.statusCode === 201) {
              message.success('Country created successfully');
              setAddCountryModalVisible(false);
              setNewCountryData({ name: '', code: '', capital: '', phoneCode: '', currency: '', coordinates: ['', ''], isEnabled: true });
              fetchCountriesData();
            } else {
              message.error(res.message || 'Failed to create country');
            }
          } catch (err) {
            message.error('Failed to create country');
          } finally {
            setAddingCountry(false);
          }
        }}
        onCancel={() => setAddCountryModalVisible(false)}
        confirmLoading={addingCountry}
      >
        <div style={{ marginBottom: 12 }}>
          <span>Country Name:</span>
          <Input
            placeholder="Enter country name"
            value={newCountryData.name}
            onChange={e => setNewCountryData({ ...newCountryData, name: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span>Country Code:</span>
          <Input
            placeholder="Enter country code (number)"
            value={newCountryData.code}
            onChange={e => setNewCountryData({ ...newCountryData, code: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span>Capital:</span>
          <Input
            placeholder="Enter capital city"
            value={newCountryData.capital}
            onChange={e => setNewCountryData({ ...newCountryData, capital: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span>Phone Code:</span>
          <Input
            placeholder="Enter phone code (e.g. +1)"
            value={newCountryData.phoneCode}
            onChange={e => setNewCountryData({ ...newCountryData, phoneCode: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span>Currency:</span>
          <Input
            placeholder="Enter currency code (e.g. USD)"
            value={newCountryData.currency}
            onChange={e => setNewCountryData({ ...newCountryData, currency: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span>Coordinates (lat, lng):</span>
          <Input
            placeholder="Latitude"
            style={{ marginBottom: 8 }}
            value={newCountryData.coordinates[0]}
            onChange={e => setNewCountryData({ ...newCountryData, coordinates: [e.target.value, newCountryData.coordinates[1]] })}
          />
          <Input
            placeholder="Longitude"
            value={newCountryData.coordinates[1]}
            onChange={e => setNewCountryData({ ...newCountryData, coordinates: [newCountryData.coordinates[0], e.target.value] })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span>Enabled:</span>
          <Switch
            checked={newCountryData.isEnabled}
            onChange={checked => setNewCountryData({ ...newCountryData, isEnabled: checked })}
          />
        </div>
      </Modal>
      <Modal
        title="Add City"
        visible={addCityModalVisible}
        onOk={async () => {
          setAddingCity(true);
          try {
            if (!newCityData.country || !newCityData.name) {
              message.error('Country and City name are required');
              setAddingCity(false);
              return;
            }
            const payload = {
              name: newCityData.name,
              country: newCityData.country,
              isEnabled: newCityData.isEnabled,
              coordinates: [
                Number(newCityData.coordinates[0]) || 0,
                Number(newCityData.coordinates[1]) || 0
              ]
            };
            const res = await createCity(payload);
            if (res.statusCode === 201) {
              message.success('City created successfully');
              setAddCityModalVisible(false);
              setNewCityData({ country: '', name: '', coordinates: ['', ''], isEnabled: true });
              fetchCountriesData();
            } else {
              message.error(res.message || 'Failed to create city');
            }
          } catch (err) {
            message.error('Failed to create city');
          } finally {
            setAddingCity(false);
          }
        }}
        onCancel={() => setAddCityModalVisible(false)}
        confirmLoading={addingCity}
      >
        <div style={{ marginBottom: 12 }}>
          <span>Country:</span>
          <select
            style={{ width: '100%', marginTop: 4 }}
            value={newCityData.country}
            onChange={e => setNewCityData({ ...newCityData, country: e.target.value })}
          >
            <option value="">Select country</option>
            {countries.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <span>City Name:</span>
          <Input
            placeholder="Enter city name"
            value={newCityData.name}
            onChange={e => setNewCityData({ ...newCityData, name: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span>Coordinates (lat, lng):</span>
          <Input
            placeholder="Latitude"
            style={{ marginBottom: 8 }}
            value={newCityData.coordinates[0]}
            onChange={e => setNewCityData({ ...newCityData, coordinates: [e.target.value, newCityData.coordinates[1]] })}
          />
          <Input
            placeholder="Longitude"
            value={newCityData.coordinates[1]}
            onChange={e => setNewCityData({ ...newCityData, coordinates: [newCityData.coordinates[0], e.target.value] })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span>Enabled:</span>
          <Switch
            checked={newCityData.isEnabled}
            onChange={checked => setNewCityData({ ...newCityData, isEnabled: checked })}
          />
        </div>
      </Modal>
      <Collapse accordion>
        {countries.map((country) => (
          <Panel
            header={
              <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <span>{country.name}</span>
                <div>
                  <Button size="small" onClick={() => handleEditClick("country", country)}>Edit</Button>
                  <Popconfirm
                    title="Update status?"
                    onConfirm={() => handleToggleStatus(country._id, country.isEnabled, "country")}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Tag color={country.isEnabled ? "green" : "red"} style={{ cursor: "pointer", marginLeft: 8 }}>
                      {country.isEnabled ? "Enabled" : "Disabled"}
                    </Tag>
                  </Popconfirm>
                </div>
              </div>
            }
            key={country._id}
          >
            <List
              dataSource={country.cities}
              renderItem={(city) => (
                <List.Item>
                  <List.Item.Meta
                    // avatar={<Avatar src="https://via.placeholder.com/50" />}
                    title={city.name}
                    description={`Coordinates: ${city.coordinates.join(", ")}`}
                  />
                  <div>
                    <Button size="small" onClick={() => handleEditClick("city", city)}>Edit</Button>
                    <Popconfirm
                      title="Update status?"
                      onConfirm={() => handleToggleStatus(city._id, city.isEnabled, "city")}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Tag color={city.isEnabled ? "green" : "red"} style={{ cursor: "pointer", marginLeft: 8 }}>
                        {city.isEnabled ? "Enabled" : "Disabled"}
                      </Tag>
                    </Popconfirm>
                  </div>
                </List.Item>
              )}
            />
          </Panel>
        ))}
      </Collapse>

      {/* Edit Modal */}
      <Modal
        title={`Edit ${editData.type === "country" ? "Country" : "City"}`}
        visible={editModalVisible}
        onOk={handleEditSave}
        onCancel={() => setEditModalVisible(false)}
      >
        <Input
          value={editData.data.name}
          onChange={(e) => setEditData({ ...editData, data: { ...editData.data, name: e.target.value } })}
          placeholder="Name"
          style={{ marginBottom: 16 }}
        />
        <Switch
          checked={editData.data.isEnabled}
          onChange={(checked) => setEditData({ ...editData, data: { ...editData.data, isEnabled: checked } })}
        />{" "}
        Enabled
      </Modal>
    </>
  );
};

export default CountriesList;